import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaTwitter, FaInstagram, FaDiscord } from "react-icons/fa";
import { FiMail, FiMapPin, FiHeart, FiArrowUp, FiZap } from "react-icons/fi";

function Footer() {
    const currentYear = new Date().getFullYear();
    const [isScrolled, setIsScrolled] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
            
            setScrollProgress(progress);
            setIsScrolled(scrollTop > 300);
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll(); // Check initial state
        
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const circumference = 2 * Math.PI * 28; // radius is 28
    const strokeDashoffset = circumference - (scrollProgress / 100) * circumference;

    return (
        <footer className="mt-auto relative overflow-hidden">
            {/* Background with cosmic gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#1e1b4b] via-[#312e81] via-[#4c1d95] to-[#581c87]"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-[#6366f1]/40 via-[#8b5cf6]/30 to-[#ec4899]/40"></div>
            <div className="absolute inset-0 bg-gradient-to-tr from-[#3b82f6]/20 via-transparent to-[#f472b6]/20"></div>
            
            {/* Decorative elements - space decorations */}
            <div className="absolute top-4 left-4 text-3xl animate-float">🎨</div>
            <div className="absolute top-8 right-8 text-2xl animate-bounce-joy">🎉</div>
            <div className="absolute bottom-4 right-4 text-3xl animate-wiggle">🎊</div>
            <div className="absolute bottom-8 left-8 text-2xl animate-twinkle">🌌</div>
            
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#fbbf24] via-[#ec4899] to-transparent opacity-80"></div>
            
            {/* Content */}
            <div className="relative z-10 border-t border-white/40 shadow-white/20">
            {/* Main Footer Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    {/* Top Section - Brand and Mission */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 mb-12 pb-12 border-b border-white/30">
                        {/* Brand Section - Larger */}
                        <div className="lg:col-span-2">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
                                <div className="relative">
                                    {/* Glowing halo effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-[#fbbf24]/40 via-[#ec4899]/30 to-[#3b82f6]/40 rounded-full blur-xl opacity-60 animate-pulse"></div>
                                    <div className="relative z-10 w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 flex items-center justify-center bg-gradient-to-br from-[#6366f1] via-[#8b5cf6] via-[#ec4899] to-[#3b82f6] rounded-2xl shadow-2xl border-4 border-[#fbbf24]/60 shadow-[#8b5cf6]/60 animate-bounce-joy">
                                        <span className="text-4xl md:text-5xl lg:text-6xl">🎮</span>
                                    </div>
                                </div>
                                <div>
                                    <h2 className="font-bold text-3xl text-white mb-2 drop-shadow-md bg-gradient-to-r from-[#fef3c7] via-white to-[#fef3c7] bg-clip-text text-transparent">JoyLoom Games</h2>
                                    <div className="w-16 h-1 bg-gradient-to-r from-[#fbbf24] via-[#ec4899] to-[#3b82f6] rounded-full shadow-lg shadow-[#8b5cf6]/60"></div>
                                </div>
                        </div>
                            <p className="text-white/95 text-base leading-relaxed mb-4 max-w-2xl">
                            Creating mobile gaming experiences that inspire, connect, and open new worlds to players worldwide.
                        </p>
                            <div className="flex items-center gap-2 text-sm text-white/90">
                            <span>Built with</span>
                            <FiHeart className="text-red-400 animate-pulse" />
                            <span>by passionate developers</span>
                        </div>
                    </div>

                        {/* Quick Contact Section */}
                        <div className="lg:col-span-1">
                            <h3 className="font-bold text-white mb-6 text-lg uppercase tracking-wide">Get in Touch</h3>
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gradient-to-br from-[#6366f1]/40 via-[#8b5cf6]/40 to-[#ec4899]/40 rounded-lg border-2 border-[#fbbf24]/50 shadow-lg shadow-[#8b5cf6]/30 flex-shrink-0 joy-hover">
                                        <FiMail className="text-white text-lg" />
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className="font-semibold text-white mb-1 text-sm">Email</h4>
                                        <a
                                            href="mailto:contact@joyloom.com"
                                            className="text-[#fef3c7] hover:text-white transition-colors text-sm block break-all"
                                        >
                                            contact@joyloom.com
                                        </a>
                                        <p className="text-xs text-white/80 mt-1">
                                            Response within 24-48h
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gradient-to-br from-[#6366f1]/40 via-[#8b5cf6]/40 to-[#ec4899]/40 rounded-lg border-2 border-[#fbbf24]/50 shadow-lg shadow-[#8b5cf6]/30 flex-shrink-0 joy-hover">
                                        <FiMapPin className="text-white text-lg" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white mb-1 text-sm">Location</h4>
                                        <p className="text-[#fef3c7] text-sm leading-relaxed">
                                            28 Brobänksvägen, Tyresö, Stockholm County<br />
                                            Sweden
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Section - Links Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
                    {/* Quick Links */}
                    <div>
                            <h3 className="font-bold text-white mb-4 text-base uppercase tracking-wide border-b border-[#fbbf24]/40 pb-2">Navigation</h3>
                            <ul className="space-y-2.5 mt-4">
                                <li>
                                    <Link 
                                        to="/" 
                                        className="text-[#fef3c7] hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-[#fbbf24] rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-[#8b5cf6]/50"></span>
                                    Home
                                </Link>
                            </li>
                            <li>
                                    <Link 
                                        to="/about" 
                                        className="text-[#fef3c7] hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-[#fbbf24] rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-[#8b5cf6]/50"></span>
                                    About Us
                                </Link>
                            </li>
                            <li>
                                    <Link 
                                        to="/contact" 
                                        className="text-[#fef3c7] hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-[#fbbf24] rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-[#8b5cf6]/50"></span>
                                    Contact
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Legal & Policies */}
                    <div>
                            <h3 className="font-bold text-white mb-4 text-base uppercase tracking-wide border-b border-[#fbbf24]/40 pb-2">Legal</h3>
                            <ul className="space-y-2.5 mt-4">
                                <li>
                                    <Link 
                                        to="/terms" 
                                        className="text-[#fef3c7] hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-[#fbbf24] rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-[#8b5cf6]/50"></span>
                                    Terms of Service
                                </Link>
                            </li>
                            <li>
                                    <Link 
                                        to="/privacy" 
                                        className="text-[#fef3c7] hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-[#fbbf24] rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-[#8b5cf6]/50"></span>
                                    Privacy Policy
                                </Link>
                            </li>
                            <li>
                                    <Link 
                                        to="/unsubscribe" 
                                        className="text-[#fef3c7] hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-[#fbbf24] rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-[#8b5cf6]/50"></span>
                                    Unsubscribe
                                </Link>
                            </li>
                        </ul>
                    </div>

                        {/* Resources Section */}
                    <div>
                            <h3 className="font-bold text-white mb-4 text-base uppercase tracking-wide border-b border-[#fbbf24]/40 pb-2">Resources</h3>
                            <ul className="space-y-2.5 mt-4">
                                <li>
                                    <a 
                                        href="/contact" 
                                        className="text-[#fef3c7] hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-[#fbbf24] rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-[#8b5cf6]/50"></span>
                                        Support Center
                                    </a>
                                </li>
                                <li>
                                    <a 
                                        href="/about" 
                                        className="text-[#fef3c7] hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-[#fbbf24] rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-[#8b5cf6]/50"></span>
                                        Our Story
                                    </a>
                                </li>
                                <li>
                                    <a 
                                        href="/contact" 
                                        className="text-[#fef3c7] hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-[#fbbf24] rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-[#8b5cf6]/50"></span>
                                        Partnership
                                    </a>
                                </li>
                            </ul>
                                </div>

                        {/* Newsletter/CTA Section */}
                                <div>
                            <h3 className="font-bold text-white mb-4 text-base uppercase tracking-wide border-b border-[#fbbf24]/40 pb-2">Stay Connected</h3>
                            <p className="text-white/90 text-sm mb-4 mt-4 leading-relaxed">
                                Follow us for the latest updates, game releases, and exclusive content.
                            </p>
                            <div className="flex flex-col gap-3">
                                <Link
                                    to="/contact"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#fbbf24] via-[#f59e0b] to-[#ec4899] hover:from-[#fcd34d] hover:via-[#fbbf24] hover:to-[#f472b6] rounded-lg text-white font-bold text-sm transition-all shadow-xl shadow-[#8b5cf6]/40 hover:shadow-2xl hover:shadow-[#ec4899]/60 hover:scale-105 joy-hover"
                                >
                                    <FiMail />
                                    Subscribe
                                </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
                <div className="border-t-4 border-[#fbbf24]/40 bg-gradient-to-r from-[#6366f1]/90 via-[#8b5cf6]/90 to-[#a855f7]/90 backdrop-blur-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        {/* Copyright */}
                            <div className="text-sm text-white/90 text-center sm:text-left order-2 sm:order-1">
                            <p>
                                © {currentYear} JoyLoom Games. All rights reserved.
                            </p>
                        </div>
                            </div>
                    </div>
                </div>
            </div>

                    {/* Fancy Star-Shaped Scroll to Top Button - Visible when scrolled */}
            {isScrolled && (
                <div className="fixed bottom-8 right-8 z-50 group">
                    {/* Multiple glowing star shadows for depth */}
                    <div 
                        className="absolute inset-0 star-shape blur-3xl opacity-60 group-hover:opacity-90 transition-opacity duration-300 -z-10 animate-twinkle"
                        style={{
                            background: `radial-gradient(circle, rgba(251, 191, 36, 1) 0%, rgba(236, 72, 153, 0.9) 50%, rgba(59, 130, 246, 0.7) 70%, transparent 85%)`,
                            width: '90px',
                            height: '90px',
                            transform: 'translate(5px, 5px)',
                        }}
                    />
                    <div 
                        className="absolute inset-0 star-shape blur-2xl opacity-50 group-hover:opacity-80 transition-opacity duration-300 -z-10 animate-pulse"
                        style={{
                            background: `radial-gradient(circle, rgba(139, 92, 246, 0.9) 0%, rgba(236, 72, 153, 0.7) 50%, rgba(251, 191, 36, 0.5) 70%, transparent 85%)`,
                            width: '85px',
                            height: '85px',
                            transform: 'translate(2px, 2px)',
                        }}
                    />
                    {/* Rotating outer glow stars */}
                    <div 
                        className="absolute inset-0 star-shape blur-xl opacity-40 -z-10 animate-star-spin"
                        style={{
                            background: `linear-gradient(45deg, rgba(251, 191, 36, 0.7), rgba(236, 72, 153, 0.6), rgba(59, 130, 246, 0.5))`,
                            width: '100px',
                            height: '100px',
                            transform: 'translate(-10px, -10px)',
                        }}
                    />
                    
                    {/* Main star button */}
                    <button
                        onClick={scrollToTop}
                        className="relative w-20 h-20 star-shape bg-gradient-to-br from-[#fbbf24] via-[#f59e0b] to-[#d97706] transition-all duration-300 flex items-center justify-center backdrop-blur-sm border-4 border-[#fef3c7]/80 overflow-visible hover:scale-125 hover:rotate-180 group/btn shadow-2xl animate-star-bounce"
                        style={{
                            boxShadow: `
                                0 15px 35px -5px rgba(251, 191, 36, 0.7),
                                0 10px 15px -8px rgba(251, 191, 36, 0.5),
                                0 0 0 3px rgba(254, 243, 199, 0.4),
                                inset 0 3px 0 rgba(255, 255, 255, 0.3),
                                inset 0 -3px 0 rgba(217, 119, 6, 0.3)
                            `,
                        }}
                        aria-label="Scroll to top"
                        title="Scroll to top! ⭐"
                    >
                        {/* Progress ring (circular around star) */}
                        <svg
                            className="absolute inset-0 w-full h-full transform -rotate-90 overflow-visible"
                            viewBox="0 0 80 80"
                            width="80"
                            height="80"
                            preserveAspectRatio="xMidYMid meet"
                        >
                            {/* Background circle */}
                            <circle
                                cx="40"
                                cy="40"
                                r="35"
                                stroke="rgba(254, 243, 199, 0.3)"
                                strokeWidth="4"
                                fill="none"
                            />
                            {/* Progress circle */}
                            <circle
                                cx="40"
                                cy="40"
                                r="35"
                                stroke="#fef3c7"
                                strokeWidth="4"
                                fill="none"
                                strokeDasharray={circumference * 1.25}
                                strokeDashoffset={circumference * 1.25 - (scrollProgress / 100) * circumference * 1.25}
                                strokeLinecap="round"
                                className="transition-all duration-150 ease-out"
                                style={{
                                    filter: "drop-shadow(0 0 6px rgba(254, 243, 199, 0.9))",
                                }}
                            />
                        </svg>
                        
                        {/* Star emoji with animation */}
                        <span className="text-4xl relative z-10 group-hover/btn:scale-150 group-hover/btn:rotate-12 transition-all duration-300 animate-twinkle drop-shadow-lg">
                            ⭐
                        </span>
                        
                        {/* Sparkle effects */}
                        <div className="absolute top-1 left-1 text-xl opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300 animate-sparkle">✨</div>
                        <div className="absolute top-1 right-1 text-xl opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500 animate-sparkle" style={{ animationDelay: '0.2s' }}>✨</div>
                        <div className="absolute bottom-1 left-1 text-xl opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500 animate-sparkle" style={{ animationDelay: '0.4s' }}>✨</div>
                        <div className="absolute bottom-1 right-1 text-xl opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500 animate-sparkle" style={{ animationDelay: '0.6s' }}>✨</div>
                        
                        {/* Inner glow */}
                        <div className="absolute inset-3 star-shape bg-gradient-to-br from-white/30 via-transparent to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                        
                        {/* Pulsing center dot */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-2 h-2 bg-white rounded-full opacity-60 group-hover/btn:opacity-100 group-hover/btn:scale-150 transition-all duration-300 animate-pulse"></div>
                        </div>
                    </button>
                    
                    {/* Floating funny emojis around the star */}
                    <div className="absolute -top-4 -left-4 text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-bounce-joy">
                        🚀
                    </div>
                    <div className="absolute -top-4 -right-4 text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-bounce-joy" style={{ animationDelay: '0.3s' }}>
                        ⬆️
                    </div>
                    <div className="absolute -bottom-4 left-0 text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-wiggle" style={{ animationDelay: '0.6s' }}>
                        💫
                    </div>
                    <div className="absolute -bottom-4 right-0 text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-float" style={{ animationDelay: '0.9s' }}>
                        🌟
                    </div>
                    
                    {/* Funny text that appears on hover */}
                    <div className="absolute -left-32 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                        <div className="bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-[#78350f] px-3 py-1 rounded-full text-xs font-bold shadow-lg border-2 border-[#fef3c7] animate-wiggle">
                            Go Up! ⬆️
                        </div>
                    </div>
                </div>
            )}
        </footer>
    );
}

export default Footer;
